import { useEffect, useState, useCallback } from "react";
import { Modal, Button, Spinner, Card, Badge, Alert } from "react-bootstrap";
import "../css/style.css";
import "../css/zerogrid.css";
import "../css/menu.css";
import "../css/modern-dashboard.css";
import api from '../api';

export default function StigmaStatusPage({host: hostName, changePage}) {
  const [apiKey, setApiKey] = useState("");
  const [showModal, setShowModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [username, setUsername] = useState("");
  const [error, setError] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [stigmaHistory, setStigmaHistory] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stigmaFilter, setStigmaFilter] = useState('all'); // 'all', 'week', 'month'
  const [stigmaFilterBasis, setStigmaFilterBasis] = useState('last'); // 'first', 'last'
  const [normalFilter, setNormalFilter] = useState('month'); // 'month', '3months', 'all'
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('all'); // 'all', 'normal', 'stigma'
  const [stigmaSort, setStigmaSort] = useState('name'); // 'name', 'days'

  const formatElapsedTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours >= 1) {
      return `${hours}시간 ${remainingMinutes}분 전`;
    } else {
      return `${minutes}분 전`;
    }
  };

  const formatStigmaDateTime = (dateTime) => {
    if (!dateTime) return "";

    const date = new Date(dateTime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;

    return `${year}-${month}-${day} ${period}${displayHours}:${minutes}`;
  };

  const calculateStigmaStartTime = (stigmaDateTime, elapsedMinutes) => {
    if (!stigmaDateTime || !elapsedMinutes) return "";

    const stigmaDate = new Date(stigmaDateTime);
    const startTime = new Date(stigmaDate.getTime() - (elapsedMinutes * 60 * 1000));

    return formatStigmaDateTime(startTime);
  };

  const calculateRemainingTime = (elapsedMinutes) => {
    const totalMinutes = 720; // 12시간
    const remaining = totalMinutes - elapsedMinutes;

    if (remaining <= 0) {
      return { hours: 0, minutes: 0, isExpired: true };
    }

    const hours = Math.floor(remaining / 60);
    const minutes = remaining % 60;

    return { hours, minutes, isExpired: false };
  };

  const getProgressPercentage = (elapsedMinutes) => {
    const totalMinutes = 720; // 12시간
    return Math.min((elapsedMinutes / totalMinutes) * 100, 100);
  };

  const calculateStigmaDays = (stigmaDateTime, lastStigmaDateTime) => {
    if (!stigmaDateTime || !lastStigmaDateTime) return 0;
    const start = new Date(stigmaDateTime);
    const end = new Date(lastStigmaDateTime);
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays + 1; // 찍힌 당일부터 1일째
  };

  const getProgressColor = (percentage) => {
    if (percentage < 33) return '#28a745'; // 녹색
    if (percentage < 66) return '#ffc107'; // 노란색
    return '#dc3545'; // 빨간색
  };

  const sortDeviceNames = (devices) => {
    return [...devices].sort((a, b) => {
      // 알파벳과 숫자를 분리 (예: "M2-3" -> ["2", "3"])
      const keyA = a.deviceName.replace(/[^0-9\-]/g, '');
      const keyB = b.deviceName.replace(/[^0-9\-]/g, '');
      const aParts = keyA.split('-').map(Number);
      const bParts = keyB.split('-').map(Number);

      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aVal = aParts[i] ?? 0;
        const bVal = bParts[i] ?? 0;
        if (aVal !== bVal) return aVal - bVal;
      }

      return 0; // 동일하면 그대로
    });
  };

  const processInput = () => {
    setLoading(true);
    setShowModal(false);

    api
      .get(`/getStigmaStatus?hostName=${hostName}&apiKey=${apiKey}`)
      .then((res) => {
        setDevices(res.data.devices || []);
        setUsername(res.data.username || "");
      })
      .catch((err) => {
        alert(err.response?.data?.error || "찍 상태 조회에 실패했습니다.");
      })
      .finally(() => setLoading(false));
  };

  const fetchStigmaStatus = useCallback(async () => {
    if (!apiKey) return;

    setError(null);

    try {
      const response = await api.get('/getStigmaStatus', {
        params: { hostName, apiKey }
      });

      setDevices(response.data.devices || []);
      setUsername(response.data.username || "");
    } catch (error) {
      console.error('찍 상태 조회 실패:', error);
      setError(error.response?.data?.error || "찍 상태 조회에 실패했습니다.");
    }
  }, [apiKey, hostName]);

  useEffect(() => {
    if (!showModal && apiKey) {
      const interval = setInterval(() => {
        fetchStigmaStatus();
      }, 30000); // 30초마다 갱신

      return () => clearInterval(interval);
    }
  }, [showModal, apiKey, fetchStigmaStatus]);

  const fetchStigmaHistory = useCallback(async (year, month) => {
    if (!apiKey) return;

    try {
      const response = await api.get('/getStigmaHistory', {
        params: {
          hostName,
          apiKey,
          year,
          month
        }
      });

      setStigmaHistory(response.data.history);
    } catch (error) {
      console.error('찍 이력 조회 실패:', error);
    }
  }, [apiKey, hostName]);

  useEffect(() => {
    if (showCalendar && apiKey) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      fetchStigmaHistory(year, month);
    }
  }, [showCalendar, currentDate, apiKey, fetchStigmaHistory]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const getStigmaColorClass = (stigmaCount, releaseCount) => {
    const netCount = stigmaCount - releaseCount;
    if (netCount <= 0) return '';
    if (netCount >= 5) return 'stigma-severe'; // 빨강
    if (netCount >= 3) return 'stigma-moderate'; // 주황
    return 'stigma-mild'; // 노랑
  };

  const getNetStigmaCount = (stigmaCount, releaseCount) => {
    return stigmaCount - releaseCount;
  };

  const handleDateClick = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    if (stigmaHistory[dateStr]) {
      setSelectedDate({
        date: dateStr,
        data: stigmaHistory[dateStr]
      });
      setShowDetailModal(true);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const stigmaDevicesAll = devices.filter(d => d.isStigma === "Y");
  const stigmaDevices = stigmaDevicesAll.filter(d => {
    if (stigmaFilter === 'all') return true;
    const dateField = stigmaFilterBasis === 'first' ? d.stigmaDateTime : d.lastStigmaDateTime;
    if (!dateField) return false;
    const diffDays = (new Date() - new Date(dateField)) / (1000 * 60 * 60 * 24);
    if (stigmaFilter === 'week') return diffDays <= 7;
    if (stigmaFilter === 'month') return diffDays <= 30;
    return true;
  });
  const normalDevicesAll = devices.filter(d => d.isStigma !== "Y");
  const normalDevices = normalDevicesAll.filter(d => {
    if (normalFilter === 'all') return true;
    if (!d.lastActivity) return false;
    const diffDays = (new Date() - new Date(d.lastActivity)) / (1000 * 60 * 60 * 24);
    if (normalFilter === 'month') return diffDays <= 30;
    if (normalFilter === '3months') return diffDays <= 90;
    return true;
  });

  // 디바이스 목록 (정상 + 찍 통합)
  const allDevicesForList = devices.filter(d => {
    // 시간 필터
    if (normalFilter !== 'all') {
      const activityDate = d.isStigma === 'Y' ? d.lastStigmaDateTime || d.stigmaDateTime : d.lastActivity;
      if (!activityDate) return false;
      const diffDays = (new Date() - new Date(activityDate)) / (1000 * 60 * 60 * 24);
      if (normalFilter === 'month' && diffDays > 30) return false;
      if (normalFilter === '3months' && diffDays > 90) return false;
    }
    // 타입 필터
    if (deviceTypeFilter === 'normal') return d.isStigma !== 'Y';
    if (deviceTypeFilter === 'stigma') return d.isStigma === 'Y';
    return true;
  });

  return (
    <div className="wrap-body modern-dashboard scrollable-page">
      {/* Loading Spinner */}
      {loading && (
        <div className="overlay modern-overlay">
          <div className="spinner-container">
            <Spinner animation="border" variant="primary" className="modern-spinner" />
            <p className="loading-text">데이터를 불러오는 중...</p>
          </div>
        </div>
      )}

      {/* API Key Modal */}
      <Modal show={showModal} onHide={() => {}} backdrop="static" keyboard={false} className="modern-modal">
        <Modal.Header className="modern-modal-header">
          <Modal.Title className="modern-modal-title">
            <i className="fas fa-exclamation-triangle me-2"></i>
            찍 상태 확인
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modern-modal-body">
          <div className="form-group modern-form-group">
            <label className="modern-label">
              <i className="fas fa-key me-2"></i>
              암호키:
            </label>
            <input
              type="password"
              className="form-control modern-input"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && processInput()}
              placeholder="암호키를 입력하세요"
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer">
          <Button variant="primary" onClick={processInput} className="modern-button">
            <i className="fas fa-sign-in-alt me-2"></i>
            확인
          </Button>
        </Modal.Footer>
      </Modal>

      <section id="container" className="modern-container">
        <div className="wrap-container">
          <div className="admin-header">
            <div className="admin-title-section">
              <h1 className="admin-title">
                <i className="fas fa-exclamation-triangle me-3"></i>
                찍 상태 확인
              </h1>
              <p className="admin-subtitle">{username || hostName}님의 디바이스 찍 상태</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="back-to-main-btn"
                onClick={() => setShowCalendar(!showCalendar)}
              >
                <i className={`fas ${showCalendar ? 'fa-list' : 'fa-calendar'} me-2`}></i>
                {showCalendar ? '현재 상태 보기' : '캘린더 보기'}
              </button>
              <button
                className="back-to-main-btn"
                onClick={() => changePage('main')}
              >
                <i className="fas fa-home me-2"></i>
                메인으로 돌아가기
              </button>
            </div>
          </div>

          <div className="zerogrid modern-grid">
          {!showModal && !showCalendar && (
            <>

              {/* Stats Cards */}
              <div className="modern-card stats-card">
                <div className="card-header">
                  <h4 className="card-title">
                    <i className="fas fa-chart-bar me-2"></i>
                    통계
                  </h4>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="text-center p-3" style={{ backgroundColor: '#fff3cd', borderRadius: '10px' }}>
                        <h3 style={{ color: '#dc3545', margin: 0, fontSize: '2.5rem' }}>
                          {stigmaDevices.length}
                        </h3>
                        <p style={{ color: '#666', marginTop: '10px', marginBottom: 0 }}>찍</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="text-center p-3" style={{ backgroundColor: '#d1e7dd', borderRadius: '10px' }}>
                        <h3 style={{ color: '#28a745', margin: 0, fontSize: '2.5rem' }}>
                          {normalDevices.length}
                        </h3>
                        <p style={{ color: '#666', marginTop: '10px', marginBottom: 0 }}>노찍</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stigma Devices */}
              {stigmaDevicesAll.length > 0 && (
                <div className="modern-card stigma-card">
                  <div className="card-header" style={{ flexWrap: 'wrap', gap: '10px' }}>
                    <h4 className="card-title">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      찍힌 디바이스
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {[
                          { key: 'all', label: '전체' },
                          { key: 'week', label: '1주일' },
                          { key: 'month', label: '1개월' },
                        ].map(opt => (
                          <button
                            key={opt.key}
                            onClick={() => setStigmaFilter(opt.key)}
                            style={{
                              padding: '4px 12px',
                              border: stigmaFilter === opt.key ? '2px solid #6366f1' : '1px solid #dee2e6',
                              borderRadius: '20px',
                              background: stigmaFilter === opt.key ? '#6366f1' : '#f8f9fa',
                              color: stigmaFilter === opt.key ? 'white' : '#666',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                        <span className="badge bg-danger ms-1">{stigmaDevices.length}대</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: '#999' }}>정렬:</span>
                        {[
                          { key: 'name', label: '이름순' },
                          { key: 'days', label: '일수순' },
                        ].map(opt => (
                          <button
                            key={opt.key}
                            onClick={() => setStigmaSort(opt.key)}
                            style={{
                              padding: '2px 8px',
                              border: stigmaSort === opt.key ? '2px solid #6f42c1' : '1px solid #dee2e6',
                              borderRadius: '12px',
                              background: stigmaSort === opt.key ? '#6f42c1' : '#f8f9fa',
                              color: stigmaSort === opt.key ? 'white' : '#999',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {stigmaFilter !== 'all' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '11px', color: '#999' }}>기준:</span>
                          {[
                            { key: 'first', label: '찍걸린 시각' },
                            { key: 'last', label: '최근찍 시각' },
                          ].map(opt => (
                            <button
                              key={opt.key}
                              onClick={() => setStigmaFilterBasis(opt.key)}
                              style={{
                                padding: '2px 8px',
                                border: stigmaFilterBasis === opt.key ? '2px solid #f59e0b' : '1px solid #dee2e6',
                                borderRadius: '12px',
                                background: stigmaFilterBasis === opt.key ? '#f59e0b' : '#f8f9fa',
                                color: stigmaFilterBasis === opt.key ? 'white' : '#999',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="card-body">
                    {stigmaDevices.length === 0 && (
                      <div className="text-center py-4" style={{ color: '#999' }}>
                        <i className="fas fa-filter" style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }}></i>
                        {stigmaFilter === 'week' ? '최근 1주일' : '최근 1개월'} 이내 찍힌 디바이스가 없습니다.
                      </div>
                    )}
                    {(stigmaSort === 'days'
                      ? [...stigmaDevices].sort((a, b) => {
                          const daysA = calculateStigmaDays(a.stigmaDateTime, a.lastStigmaDateTime);
                          const daysB = calculateStigmaDays(b.stigmaDateTime, b.lastStigmaDateTime);
                          return daysB - daysA; // 내림차순 (오래된 것이 먼저)
                        })
                      : sortDeviceNames(stigmaDevices)
                    ).map((device, index) => {
                      const elapsedMinutes = device.elapsedMinutes || 0;
                      const stigmaDateTime = device.stigmaDateTime || 0;
                      const lastStigmaDateTime = device.lastStigmaDateTime || 0;
                      const remaining = calculateRemainingTime(elapsedMinutes);
                      const progress = getProgressPercentage(elapsedMinutes);
                      const progressColor = getProgressColor(progress);

                      const stigmaDays = calculateStigmaDays(stigmaDateTime, lastStigmaDateTime);

                      return (
                        <div key={index} className="device-item mb-2 px-3 py-2" style={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          borderLeft: `4px solid ${progressColor}`
                        }}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div>
                              <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {device.deviceName}
                                {stigmaDays > 0 && (
                                  <span style={{
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    color: stigmaDays >= 7 ? '#dc3545' : stigmaDays >= 3 ? '#fd7e14' : '#6c757d',
                                    backgroundColor: stigmaDays >= 7 ? '#fff5f5' : stigmaDays >= 3 ? '#fff8f0' : '#f8f9fa',
                                    padding: '1px 6px',
                                    borderRadius: '10px',
                                    border: `1px solid ${stigmaDays >= 7 ? '#f5c2c7' : stigmaDays >= 3 ? '#ffe0b2' : '#dee2e6'}`
                                  }}>
                                    {stigmaDays}일째
                                  </span>
                                )}
                              </div>
                              {device.stigmaDateTime && (
                                <div style={{ fontSize: '11px', color: '#999', lineHeight: '1.4' }}>
                                  <span style={{ color: '#dc3545', fontWeight: '600' }}>
                                    찍: {formatStigmaDateTime(device.stigmaDateTime)}
                                  </span>
                                  {' / '}최근: {formatStigmaDateTime(lastStigmaDateTime)}
                                </div>
                              )}
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                사냥 {formatElapsedTime(elapsedMinutes)}
                              </div>
                            </div>
                            <div className="text-end" style={{ whiteSpace: 'nowrap' }}>
                              {!remaining.isExpired ? (
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: progressColor }}>
                                  {remaining.hours}h {remaining.minutes}m
                                </div>
                              ) : (
                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#28a745' }}>
                                  곧 해제
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div style={{
                            width: '100%',
                            height: '10px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '5px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${progress}%`,
                              height: '100%',
                              backgroundColor: progressColor,
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Device List (Combined) */}
              {devices.length > 0 && (
                <div className="modern-card normal-card">
                  <div className="card-header" style={{ flexWrap: 'wrap', gap: '10px' }}>
                    <h4 className="card-title">
                      <i className="fas fa-list me-2"></i>
                      디바이스 목록
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {[
                          { key: 'month', label: '한달' },
                          { key: '3months', label: '3개월' },
                          { key: 'all', label: '전체' },
                        ].map(opt => (
                          <button
                            key={opt.key}
                            onClick={() => setNormalFilter(opt.key)}
                            style={{
                              padding: '4px 12px',
                              border: normalFilter === opt.key ? '2px solid #6366f1' : '1px solid #dee2e6',
                              borderRadius: '20px',
                              background: normalFilter === opt.key ? '#6366f1' : '#f8f9fa',
                              color: normalFilter === opt.key ? 'white' : '#666',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {[
                          { key: 'all', label: '전체', color: '#6c757d' },
                          { key: 'normal', label: '정상', color: '#28a745' },
                          { key: 'stigma', label: '찍', color: '#dc3545' },
                        ].map(opt => (
                          <button
                            key={opt.key}
                            onClick={() => setDeviceTypeFilter(opt.key)}
                            style={{
                              padding: '3px 10px',
                              border: deviceTypeFilter === opt.key ? `2px solid ${opt.color}` : '1px solid #dee2e6',
                              borderRadius: '15px',
                              background: deviceTypeFilter === opt.key ? opt.color : '#f8f9fa',
                              color: deviceTypeFilter === opt.key ? 'white' : '#666',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                        <span className="badge bg-secondary ms-1">{allDevicesForList.length}대</span>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {allDevicesForList.length === 0 && (
                      <div className="text-center py-3" style={{ color: '#999' }}>
                        <i className="fas fa-filter" style={{ fontSize: '20px', marginBottom: '8px', display: 'block' }}></i>
                        조건에 맞는 디바이스가 없습니다.
                      </div>
                    )}
                    <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                      {sortDeviceNames(allDevicesForList).map((device, index) => (
                        <Badge
                          key={index}
                          bg={device.isStigma === 'Y' ? 'danger' : 'success'}
                          style={{
                            fontSize: '14px',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            color: 'white'
                          }}
                        >
                          {device.deviceName}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {devices.length === 0 && !loading && (
                <div className="modern-card empty-card">
                  <div className="card-body text-center" style={{ padding: '60px 20px' }}>
                    <i className="fas fa-inbox" style={{ fontSize: '48px', color: '#999', marginBottom: '20px' }}></i>
                    <h5 style={{ color: '#999' }}>최근 활동한 디바이스가 없습니다.</h5>
                  </div>
                </div>
              )}

            </>
          )}

          {/* Calendar View */}
          {!showModal && showCalendar && (
            <div className="modern-card calendar-card">
              <div className="card-header">
                <h4 className="card-title">
                  <i className="fas fa-calendar me-2"></i>
                  찍 이력 캘린더
                </h4>
              </div>
              <div className="card-body">
                {/* Calendar Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <button
                    onClick={handlePrevMonth}
                    style={{
                      background: 'transparent',
                      border: '1px solid #dee2e6',
                      borderRadius: '5px',
                      padding: '8px 15px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '600' }}>
                    {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
                  </h3>
                  <button
                    onClick={handleNextMonth}
                    style={{
                      background: 'transparent',
                      border: '1px solid #dee2e6',
                      borderRadius: '5px',
                      padding: '8px 15px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>

                {/* Calendar Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '5px'
                }}>
                  {/* Week Days Header */}
                  {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
                    <div key={day} style={{
                      textAlign: 'center',
                      padding: '10px',
                      fontWeight: '600',
                      color: index === 0 ? '#dc3545' : index === 6 ? '#007bff' : '#666',
                      fontSize: '0.9rem'
                    }}>
                      {day}
                    </div>
                  ))}

                  {/* Calendar Days */}
                  {(() => {
                    const firstDay = getFirstDayOfMonth(currentDate);
                    const daysInMonth = getDaysInMonth(currentDate);
                    const days = [];

                    // Empty cells before first day
                    for (let i = 0; i < firstDay; i++) {
                      days.push(
                        <div key={`empty-${i}`} style={{
                          padding: '10px',
                          minHeight: '80px'
                        }}></div>
                      );
                    }

                    // Days of month
                    for (let day = 1; day <= daysInMonth; day++) {
                      const year = currentDate.getFullYear();
                      const month = currentDate.getMonth() + 1;
                      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const dayData = stigmaHistory[dateStr];
                      const stigmaCount = dayData?.stigmaCount || 0;
                      const releaseCount = dayData?.releaseCount || 0;
                      const netCount = getNetStigmaCount(stigmaCount, releaseCount);
                      const hasActivity = dayData && (stigmaCount > 0 || releaseCount > 0);

                      days.push(
                        <div
                          key={day}
                          onClick={() => hasActivity && handleDateClick(day)}
                          style={{
                            padding: '10px',
                            minHeight: '80px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            cursor: hasActivity ? 'pointer' : 'default',
                            transition: 'all 0.2s',
                            background: netCount > 0
                              ? netCount >= 5
                                ? '#ffe0e0'
                                : netCount >= 3
                                ? '#fff3cd'
                                : '#fff9e6'
                              : 'white',
                            ':hover': {
                              transform: hasActivity ? 'scale(1.05)' : 'none'
                            }
                          }}
                          className={hasActivity ? 'calendar-day-hover' : ''}
                        >
                          <div style={{
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            marginBottom: '5px',
                            color: '#333'
                          }}>
                            {day}
                          </div>
                          {hasActivity && (
                            <div style={{
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              textAlign: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '3px'
                            }}>
                              {stigmaCount > 0 && (
                                <div style={{
                                  padding: '2px 5px',
                                  background: '#f8d7da',
                                  borderRadius: '4px',
                                  color: '#dc3545'
                                }}>
                                  <i className="fas fa-arrow-up me-1"></i>
                                  +{stigmaCount}
                                </div>
                              )}
                              {releaseCount > 0 && (
                                <div style={{
                                  padding: '2px 5px',
                                  background: '#d1e7dd',
                                  borderRadius: '4px',
                                  color: '#28a745'
                                }}>
                                  <i className="fas fa-arrow-down me-1"></i>
                                  -{releaseCount}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return days;
                  })()}
                </div>

                {/* Legend */}
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  display: 'flex',
                  gap: '20px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      padding: '3px 8px',
                      background: '#f8d7da',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      color: '#dc3545',
                      fontWeight: '600'
                    }}>
                      <i className="fas fa-arrow-up"></i> +3
                    </div>
                    <span style={{ fontSize: '0.85rem' }}>찍 걸림</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      padding: '3px 8px',
                      background: '#d1e7dd',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      color: '#28a745',
                      fontWeight: '600'
                    }}>
                      <i className="fas fa-arrow-down"></i> -2
                    </div>
                    <span style={{ fontSize: '0.85rem' }}>찍 해제</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #dc3545, #e74c3c)', color: 'white' }}>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {selectedDate?.date} 찍 이력
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDate?.data && (
            <>
              <div style={{
                marginBottom: '20px',
                padding: '15px',
                background: '#f8f9fa',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>찍 걸림</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#dc3545' }}>
                    +{selectedDate.data.stigmaCount || 0}
                  </div>
                </div>
                <div style={{ fontSize: '1.5rem', color: '#dee2e6', fontWeight: '300' }}>|</div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>찍 해제</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#28a745' }}>
                    -{selectedDate.data.releaseCount || 0}
                  </div>
                </div>
              </div>

              {/* 찍 걸림 */}
              {selectedDate.data.stigmaDevices && selectedDate.data.stigmaDevices.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h6 style={{
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#dc3545',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <i className="fas fa-arrow-up"></i>
                    찍 걸림 ({selectedDate.data.stigmaDevices.length}건)
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedDate.data.stigmaDevices.map((device, index) => (
                      <div key={index} style={{
                        padding: '10px',
                        background: '#fff5f5',
                        borderRadius: '6px',
                        borderLeft: '3px solid #dc3545'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '3px' }}>
                          {device.deviceName}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          {formatStigmaDateTime(device.dateTime)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 찍 해제 */}
              {selectedDate.data.releaseDevices && selectedDate.data.releaseDevices.length > 0 && (
                <div>
                  <h6 style={{
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#28a745',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <i className="fas fa-arrow-down"></i>
                    찍 해제 ({selectedDate.data.releaseDevices.length}건)
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedDate.data.releaseDevices.map((device, index) => (
                      <div key={index} style={{
                        padding: '10px',
                        background: '#f0f9f4',
                        borderRadius: '6px',
                        borderLeft: '3px solid #28a745'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '3px' }}>
                          {device.deviceName}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          {formatStigmaDateTime(device.dateTime)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
